import { z } from 'zod';

// Request lifecycle states. `delivered` marks the deliverable as published to
// the client; `closed` is the formal terminal state set after the engagement
// is fully wrapped (final invoice settled, deliverables signed off, no
// follow-on work expected). Both are "not open" for the open_requests counter.
export const REQUEST_STATUSES = ['submitted', 'reviewing', 'engagement', 'delivered', 'closed'] as const;
export const RequestStatusSchema = z.enum(REQUEST_STATUSES);
export type RequestStatus = z.infer<typeof RequestStatusSchema>;

// Helper: a request counts as "open" while the client still expects work.
// Used by the open_requests counter on Client and by any UI that wants to
// hide finished briefs from default views.
export const TERMINAL_REQUEST_STATUSES: readonly RequestStatus[] = ['delivered', 'closed'];
export function isOpenRequestStatus(s: RequestStatus): boolean {
  return !TERMINAL_REQUEST_STATUSES.includes(s);
}

// Urgency drives expected lead-time and possible fee surcharge.
export const REQUEST_URGENCIES = ['standard', 'expedited', 'exploratory'] as const;
export const RequestUrgencySchema = z.enum(REQUEST_URGENCIES);
export type RequestUrgency = z.infer<typeof RequestUrgencySchema>;

// Triage state used by the operator inbox (separate from the client-visible
// status). 'unread' / 'reviewing' / 'proposed' map to chips in admin inbox.
export const REQUEST_TRIAGE = ['unread', 'reviewing', 'proposed', 'rejected'] as const;
export const RequestTriageSchema = z.enum(REQUEST_TRIAGE);
export type RequestTriage = z.infer<typeof RequestTriageSchema>;

// Submit input from the portal "New Request" wizard. The eligibility flags
// are gating: literal(true) means the API rejects any submission with them
// missing or false (constraint #1 in the brief — B2B-only gating).
export const RequestInputSchema = z.object({
  service: z.string().min(1, 'Service is required'),
  urgency: RequestUrgencySchema.default('standard'),
  headline: z.string().min(1, 'Engagement headline is required').max(200),
  description: z.string().min(20, 'Description must be at least 20 characters').max(5000),
  counterparty: z.string().max(200).optional(),

  body_corporate_confirmed: z.literal(true, {
    errorMap: () => ({ message: 'Body-corporate confirmation is required.' }),
  }),
  sophisticated_self_cert: z.literal(true, {
    errorMap: () => ({ message: 'Sophisticated / HNW corporate self-certification is required.' }),
  }),
  not_advice_acknowledged: z.literal(true, {
    errorMap: () => ({ message: 'You must acknowledge Omega is not FCA-authorised and gives no investment advice.' }),
  }),
  kyc_acknowledged: z.literal(true, {
    errorMap: () => ({ message: 'KYC acknowledgement is required.' }),
  }),
});
export type RequestInput = z.infer<typeof RequestInputSchema>;

// Public-safe shape returned by GET /api/requests, GET /api/requests/:id, etc.
export const RequestDtoSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  client_name: z.string().optional(), // Joined for staff list views.
  created_by_user_id: z.string(),

  service: z.string(),
  urgency: RequestUrgencySchema,
  headline: z.string(),
  description: z.string(),
  counterparty: z.string().nullable().optional(),

  status: RequestStatusSchema,
  triage: RequestTriageSchema,

  assigned_lead_staff_id: z.string().nullable().optional(),
  // Resolved staff lead for client-facing surfaces (name + role label +
  // office). Populated only on the single-request GET; list endpoints omit it.
  assigned_lead: z
    .object({
      id: z.string(),
      name: z.string(),
      role: z.string(),
      office: z.string().nullable(),
    })
    .nullable()
    .optional(),
  value_pence: z.number().int().nonnegative().nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
});
export type RequestDto = z.infer<typeof RequestDtoSchema>;

// Filters for GET /api/requests/inbox (operator)
export const RequestQuerySchema = z.object({
  status: RequestStatusSchema.optional(),
  triage: RequestTriageSchema.optional(),
  client_id: z.string().optional(),
  assigned_lead_staff_id: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type RequestQuery = z.infer<typeof RequestQuerySchema>;
